import React, { useState, useEffect } from "react";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { FeatherEdit } from "@subframe/core";
import { FeatherSave } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { FeatherInfo } from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import ReviewChangesModal, { ChangeItem } from "./ReviewChangesModal";
import { ReviewPendingInlineValue } from "./ReviewFieldDiffHint";
import { isValidGstinFormat } from "../../../utils/gstinUtils";

export type TaxInfoFieldDiffSlice = { oldText: string; newText: string };

export interface CustomerGstinHint {
  show: boolean;
  label: string;
  tooltip: string;
}

interface TaxInfoData {
  vendorGstin: string;
  customerGstin: string;
  // gstRate: string;
  taxBreakdown: string;
}

interface TaxInfoOptionsData {
  vendorGstin: string[];
  customerGstin: string[];
  gstRate: string[];
  taxBreakdown: string[];
}

interface TaxInfoEditableFieldProps {
  initialData: TaxInfoData;
  isGstEnabled: boolean;
  optionsData?: TaxInfoOptionsData;
  onSave?: (newData: TaxInfoData) => void;
  lineItems?: any[];
  currencySymbol?: string;
  disabled?: boolean;
  disableCustomerGstin?: boolean; // Disable customer GSTIN editing (for invoices)
  disableVendorGstin?: boolean; // Disable vendor GSTIN editing (for bills)
  /** FBAC: vendor GSTIN delta (JSON key vendor_gstin) */
  fieldDiffs?: Partial<Record<"vendor_gstin", TaxInfoFieldDiffSlice>>;
  pendingReviewDiffActive?: boolean;
  showGstinFormatWarning?: boolean;
  customerGstinHint?: CustomerGstinHint;
}

const TaxInfoEditableField: React.FC<TaxInfoEditableFieldProps> = ({
  initialData = {
    vendorGstin: "29ABCDE1234F1Z5",
    customerGstin: "27FGHIJ5678K1L9",
    // gstRate: "18%",
    taxBreakdown: "CGST: $400, SGST: $400",
  },
  optionsData = {
    vendorGstin: [],
    customerGstin: [],
    gstRate: [],
    taxBreakdown: [],
  },
  onSave,
  lineItems = [],
  isGstEnabled,
  currencySymbol = "₹",
  disabled = false,
  disableCustomerGstin = false,
  disableVendorGstin = false,
  fieldDiffs,
  pendingReviewDiffActive = false,
  showGstinFormatWarning = false,
  customerGstinHint,
}) => {
  // Calculate tax breakdown from line items: sum of (rate * quantity * tax_percentage / 100)
  const calculatedTaxBreakdown = lineItems.reduce((sum, item) => {
    const taxPercentage = item.tax_percentage || 0;
    const taxAmount =
      (item.rate || 0) * (item.quantity || 0) * (taxPercentage / 100);
    return sum + taxAmount;
  }, 0);

  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<TaxInfoData>(initialData);
  const [editData, setEditData] = useState<TaxInfoData>(initialData);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<ChangeItem[]>([]);

  // Sync internal state with initialData and calculated tax when they change
  useEffect(() => {
    setData({
      vendorGstin: initialData.vendorGstin,
      customerGstin: initialData.customerGstin,
      taxBreakdown: `Total Tax: ${currencySymbol}${calculatedTaxBreakdown.toFixed(
        2
      )}`,
    });
  }, [
    initialData.vendorGstin,
    initialData.customerGstin,
    calculatedTaxBreakdown,
    currencySymbol,
  ]);

  const handleEdit = () => {
    setEditData(data);
    setIsEditing(true);
  };

  const handleSave = () => {
    // Check for changes and create change items
    const changes: ChangeItem[] = [];

    Object.keys(editData).forEach((key) => {
      const fieldKey = key as keyof TaxInfoData;
      if (data[fieldKey] !== editData[fieldKey]) {
        changes.push({
          field:
            key.charAt(0).toUpperCase() +
            key.slice(1).replace(/([A-Z])/g, " $1"),
          previousValue: data[fieldKey],
          newValue: editData[fieldKey],
        });
      }
    });

    if (changes.length > 0) {
      setPendingChanges(changes);
      setIsReviewModalOpen(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditData(data);
    setIsEditing(false);
  };

  const handleConfirmChanges = () => {
    setData(editData);
    setIsEditing(false);
    setIsReviewModalOpen(false);
    setPendingChanges([]);
    // Optional callback to parent component this will update the json !! to be used to send to DB later
    onSave?.(editData);
  };

  const handleCancelChanges = () => {
    setIsReviewModalOpen(false);
    setPendingChanges([]);
    // Keep editing mode open so user can continue editing
  };

  const handleFieldChange = (field: keyof TaxInfoData, value: string) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const isFieldEmpty = (value: string) => !value || value.trim() === "";

  const getSuggestions = (field: keyof TaxInfoOptionsData) => {
    return optionsData[field] || [];
  };

  const shouldShowSuggestion = (field: keyof TaxInfoData) => {
    if (isFieldEmpty(data[field])) {
      const optionsField = field as keyof TaxInfoOptionsData;
      if (optionsData[optionsField].length > 0) {
        return true;
      }
    }
    return false;
  };

  const getGstinWarning = (value: string, fieldDisabled: boolean) => {
    if (!showGstinFormatWarning || fieldDisabled) {
      return "";
    }

    const trimmed = value.trim();
    if (!trimmed || isValidGstinFormat(trimmed)) {
      return "";
    }

    return "GSTIN seems invalid. Enter a valid 15-character GSTIN, or leave it blank if not available.";
  };

  const getGstinDisplayValue = (value: string) => {
    if (!showGstinFormatWarning) {
      return value;
    }

    return value.trim() ? value : "Not provided";
  };

  const vendorGstinWarning = getGstinWarning(
    editData.vendorGstin,
    disableVendorGstin
  );
  const customerGstinWarning = getGstinWarning(
    editData.customerGstin,
    disableCustomerGstin
  );

  return (
    <div className="flex w-full flex-col items-start gap-3 border-b border-solid border-neutral-100 pb-4">
      <div className="flex w-full items-center justify-between">
        <span className="text-body-bold font-body-bold text-subtext-color">
          Tax Information
        </span>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Button variant="brand-primary" size="small" onClick={handleSave}>
              <FeatherSave />
            </Button>
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={handleCancel}
            >
              <FeatherX />
            </Button>
          </div>
        ) : (
          !disabled && (
            <FeatherEdit
              className="text-body-bold font-body-bold text-subtext-color cursor-pointer hover:text-brand-600 transition-colors"
              onClick={handleEdit}
            />
          )
        )}
      </div>
      <div className="flex w-full flex-col gap-4">
        {isGstEnabled && (
          <div className="grid w-full grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
            <div className="flex min-w-0 flex-col items-start gap-1">
              <div className="flex items-center gap-1.5">
                <span className="whitespace-nowrap text-body font-body text-subtext-color">
                  Vendor GSTIN
                </span>
                {shouldShowSuggestion("vendorGstin") && (
                  <SubframeCore.Tooltip.Provider>
                    <SubframeCore.Tooltip.Root>
                      <SubframeCore.Tooltip.Trigger asChild={true}>
                        <FeatherInfo className="text-body font-body text-error-700" />
                      </SubframeCore.Tooltip.Trigger>
                      <SubframeCore.Tooltip.Portal>
                        <SubframeCore.Tooltip.Content
                          side="top"
                          align="center"
                          sideOffset={4}
                          asChild={true}
                        >
                          <div className="flex min-w-[240px] flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-white px-3 py-2 shadow-md">
                            <span className="text-caption-bold font-caption-bold text-default-font">
                              Possible Matches:
                            </span>
                            <div className="flex flex-col items-start gap-1">
                              {getSuggestions("vendorGstin").map(
                                (suggestion, index) => (
                                  <span
                                    key={index}
                                    className="text-caption font-caption text-brand-600 cursor-pointer hover:text-brand-800"
                                    onClick={() => {
                                      if (isEditing) {
                                        handleFieldChange(
                                          "vendorGstin",
                                          suggestion,
                                        );
                                      } else {
                                        setData((prev) => ({
                                          ...prev,
                                          vendorGstin: suggestion,
                                        }));
                                      }
                                    }}
                                  >
                                    • {suggestion}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        </SubframeCore.Tooltip.Content>
                      </SubframeCore.Tooltip.Portal>
                    </SubframeCore.Tooltip.Root>
                  </SubframeCore.Tooltip.Provider>
                )}
              </div>
              {isEditing ? (
                <TextField
                  label=""
                  helpText={vendorGstinWarning}
                  error={!!vendorGstinWarning}
                >
                  <TextField.Input
                    placeholder="29ABCDE1234F1Z5"
                    value={editData.vendorGstin}
                    onChange={(e) =>
                      handleFieldChange("vendorGstin", e.target.value)
                    }
                    onKeyDown={handleKeyPress}
                    autoFocus
                    disabled={disableVendorGstin}
                  />
                </TextField>
              ) : data.vendorGstin.length < 0 ? (
                <div className="flex items-start rounded-md bg-error-50 px-2 py-1 border-2 border-error-500">
                  <span className="text-body font-body text-error-700">
                    [Empty - Needs Review]
                  </span>
                </div>
              ) : (
                <ReviewPendingInlineValue
                  pendingReviewDiffActive={pendingReviewDiffActive}
                  isEditing={isEditing}
                  oldText={fieldDiffs?.vendor_gstin?.oldText ?? ""}
                  newText={fieldDiffs?.vendor_gstin?.newText ?? ""}
                >
                  <span className="text-body-bold font-body-bold text-default-font">
                    {getGstinDisplayValue(data.vendorGstin)}
                  </span>
                </ReviewPendingInlineValue>
              )}
            </div>
            <div className="flex min-w-0 flex-col items-start gap-1">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="shrink-0 whitespace-nowrap text-body font-body text-subtext-color">
                  Customer GSTIN
                </span>
                {customerGstinHint?.show && (
                  <SubframeCore.Tooltip.Provider>
                    <SubframeCore.Tooltip.Root>
                      <SubframeCore.Tooltip.Trigger asChild={true}>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-medium leading-none text-warning-800 ring-1 ring-inset ring-warning-200 bg-warning-50 hover:bg-warning-100 transition-colors"
                        >
                          {customerGstinHint.label}
                        </button>
                      </SubframeCore.Tooltip.Trigger>
                      <SubframeCore.Tooltip.Portal>
                        <SubframeCore.Tooltip.Content
                          side="top"
                          align="start"
                          sideOffset={4}
                          asChild={true}
                        >
                          <div className="flex max-w-[280px] flex-col items-start rounded-md border border-solid border-neutral-border bg-white px-3 py-2 shadow-md">
                            <span className="text-caption font-caption text-default-font">
                              {customerGstinHint.tooltip}
                            </span>
                          </div>
                        </SubframeCore.Tooltip.Content>
                      </SubframeCore.Tooltip.Portal>
                    </SubframeCore.Tooltip.Root>
                  </SubframeCore.Tooltip.Provider>
                )}
              </div>
              {isEditing ? (
                <TextField
                  label=""
                  helpText={customerGstinWarning}
                  error={!!customerGstinWarning}
                >
                  <TextField.Input
                    placeholder="27FGHIJ5678K1L9"
                    value={editData.customerGstin}
                    onChange={(e) =>
                      handleFieldChange("customerGstin", e.target.value)
                    }
                    onKeyDown={handleKeyPress}
                    disabled={disableCustomerGstin}
                  />
                </TextField>
              ) : data.customerGstin.length < 0 ? (
                <div className="flex items-start rounded-md bg-error-50 px-2 py-1 border-2 border-error-500">
                  <span className="text-body font-body text-error-700">
                    [Empty - Needs Review]
                  </span>
                </div>
              ) : (
                <span className="text-body-bold font-body-bold text-default-font">
                  {getGstinDisplayValue(data.customerGstin)}
                </span>
              )}
            </div>
          </div>
        )}
        {/* <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <span className="text-body font-body text-subtext-color">
            GST Rate
          </span>
          {isEditing ? (
            <TextField label="" helpText="">
              <TextField.Input
                placeholder="18%"
                value={editData.gstRate}
                onChange={(e) => handleFieldChange("gstRate", e.target.value)}
                onKeyDown={handleKeyPress}
              />
            </TextField>
          ) : data.gstRate.length < 0 ? (
            <div className="flex items-start rounded-md bg-error-50 px-2 py-1 border-2 border-error-500">
              <span className="text-body font-body text-error-700">
                [Empty - Needs Review]
              </span>
            </div>
          ) : (
            <span className="text-body-bold font-body-bold text-default-font">
              {data.gstRate}
            </span>
          )}
        </div> */}
        <div className="flex w-full flex-col items-start gap-1">
          <span className="text-body font-body text-subtext-color">
            Tax Breakdown
          </span>
          <span className="text-body-bold font-body-bold text-default-font">
            {isGstEnabled ? data.taxBreakdown : "No tax breakdown"}
          </span>
        </div>
      </div>

      {/* Review Changes Modal */}
      <ReviewChangesModal
        open={isReviewModalOpen}
        onOpenChange={setIsReviewModalOpen}
        changes={pendingChanges}
        onConfirm={handleConfirmChanges}
        onCancel={handleCancelChanges}
        title="Review Tax Information Changes"
        description="These changes will affect the invoice calculations and tax implications. Please review carefully before proceeding."
      />
    </div>
  );
};

export default TaxInfoEditableField;
